import { useState } from 'react';

import { useMutation, gql } from '@zenbusiness/application-commons-graphql';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';

export const UPDATE_ALL_PARTNER_OPT_OUT = gql`
mutation UpdateAllPartnerOptOutMutation($input: Boolean!) {
  updateAllPartnerOptOut(input: $input)
}
`;

const useStyles = makeStyles((theme) => ({
  baseDialog: {
    backgroundColor: theme.palette.focus10,
    borderRadius: 0,
    [theme.breakpoints.up('sm')]: {
      borderRadius: 16
    }
  },
  title: {
    ...theme.typography.title_medium,
    [theme.breakpoints.up('sm')]: {
      ...theme.typography.title_large
    }
  },
  MuiDialogTitle: {
    padding: '48px 32px 8px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 16,
      paddingRight: 16
    }
  },
  MuiDialogContent: {
    padding: '8px 32px 16px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 16,
      paddingRight: 16
    }
  },
  MuiDialogActions: {
    padding: '16px 32px 32px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 16,
      paddingRight: 16
    }
  },
  boldText: {
    marginBottom: 4,
    fontWeight: 600,
    ...theme.typography.body_extraLargeRegular
  },
  bodyText: {
    ...theme.typography.body_largeRegular,
    '&&': {
      marginBottom: 16
    }
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 10,
    [theme.breakpoints.up('sm')]: {
      top: 18,
      right: 16
    }
  }
}));

const OptOutModal = ({
  isOpen,
  onClose,
  refresh
}) => {
  const [exception, setException] = useState(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();

  const [updateAllPartnerOptOut] = useMutation(UPDATE_ALL_PARTNER_OPT_OUT);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await updateAllPartnerOptOut({
        variables: {
          input: true
        },
        options: {
          // this is to ensure the account object is refreshed after submitting the opt out request
          onCompleted: setTimeout(() => {
            refresh();
          }, 2000)
        }
      });
      onClose();
    } catch (err) {
      setException(err);
    }
  };

  if (exception) throw exception;

  return (
    <Dialog
      open={isOpen}
      scroll="paper"
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby="partner-opt-out-title"
      aria-describedby="partner-opt-out-body"
      PaperProps={{
        className: classes.baseDialog
      }}
      data-testid="opt-out"
    >
      <DialogTitle
        id="partner-opt-out-title"
        className={classes.MuiDialogTitle}
      >
        <Title variant="md-alt" component="p" level={2} className={classes.title}>
          Do not share or sell my personal information.
        </Title>
        <Button
          color="primary"
          size="small"
          variant="text"
          onClick={onClose}
          className={classes.closeButton}
        >
          Close
        </Button>
        <Divider aria-hidden style={{ marginBottom: 0 }} />
      </DialogTitle>
      <DialogContent
        id="partner-opt-out-body"
        className={classes.MuiDialogContent}
      >
        <Title variant="sm" component="p" level={3} className={classes.boldText}>
          California Consumer Privacy Act
        </Title>
        <Text size="sm" component="p" className={classes.bodyText}>
          The California Consumer Privacy Act of 2020 (CPRA) provides individual with a right to
          opt-out of the &quot;sharing&quot; or &quot;selling&quot; of personal information.
          The CPRA defines data sharing as any disclosure of personal information (renting,
          releasing, disclosing, disseminating, making available, transferring, or otherwise
          communicating orally, in writing, or by electronic or other means) to third parties.
          A &quot;sale&quot; is defined as the &quot;selling, renting, releasing, disclosing,
          disseminating, making available, transferring, or otherwise communicating orally,
          in writing,or by electronic or other means, a consumer&apos;s personal information by
          the business to another business or a third party for monetary or other
          valuable consideration.&quot;
        </Text>
        <Title variant="sm" level={3} className={classes.boldText}>
          How to opt-out of &quot;sharing&quot; or &quot;selling&quot; of personal information
        </Title>
        <Text size="sm" component="p" className={classes.bodyText}>
          You may submit your request to opt-out of potential &quot;sales&quot; of your personal
          information by entering your email address below. To protect your privacy, we take
          proactive measures to keep clear-text email addresses out of our system. Once you submit
          the form below, your email address will be automatically hashed (obfuscated) and sent
          to us. An automated message will then be emailed to you with a link to verify your
          request. Please click on that link within 30 days. Once you have verified your request,
          we will no longer “sell” any data we have associated with your hashed email address.
        </Text>
      </DialogContent>
      <DialogActions
        className={classes.MuiDialogActions}
      >
        <Grid container direction="row-reverse" spacing={2}>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              color="primary"
              size="large"
              variant="contained"
              type="submit"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              color="primary"
              size="large"
              variant="text"
              onClick={onClose}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

OptOutModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired
};
export default OptOutModal;
